import React, { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAuth } from "../../redux/slices/authSlice";
import DashboardLayout from "../../components/DashboardLayout";
import FleetrifyCard from "../../components/Card";
import {
  useGetInsightsSummaryQuery,
  useAskInsightsMutation,
  InsightsContext,
} from "../../redux/services/insightsApi";
import { useGetCompanyInfoQuery } from "../../redux/services/settingsApi";
import { Download, Send, Bot, User, Loader2, RefreshCw, Sparkles, CalendarDays, X, Lock } from "lucide-react";
import { FLEETRIFYROUTEPATH } from "../../routes/routesPath";
import { generateInsightsPPTX } from "../../utils/generateInsightsPPTX";

const formatDriverRating = (rating: number | null): string =>
  rating == null ? "No ratings yet" : `⭐ ${rating}`;

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  context?: InsightsContext;
}

const Insights = () => {
  const navigate = useNavigate();
  const auth = useSelector(selectAuth);
  const planName = auth?.user?.company?.latestSubscription_plan?.name;
  const hasAccess = planName === "Individual Business";

  const [question, setQuestion] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "welcome",
      role: "assistant",
      content:
        "Hi! I'm your Fleetrify AI assistant powered by Fleetrify. Ask me anything about your fleet, orders, revenue, or operations — I'll give you instant insights.",
      timestamp: new Date(),
    },
  ]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } =
    useGetInsightsSummaryQuery();
  const [askInsights, { isLoading: askLoading }] = useAskInsightsMutation();

  const { data: companyInfoData } = useGetCompanyInfoQuery();
  const companyName: string =
    (companyInfoData?.data?.companyInfo as any)?.company_name ||
    (companyInfoData?.data?.companyInfo as any)?.name ||
    "Fleetrify";

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const trimmed = question.trim();
    if (!trimmed || askLoading) return;

    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: trimmed,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setQuestion("");

    try {
      const payload: { question: string; from?: string; to?: string } = { question: trimmed };
      if (dateFrom) payload.from = dateFrom;
      if (dateTo) payload.to = dateTo;
      const result = await askInsights(payload).unwrap();
      const aiMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: result.data.answer,
        context: result.data.context,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiMsg]);
    } catch {
      const errMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "Sorry, I couldn't process your request. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errMsg]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleExport = async () => {
    if (!summary) return;
    setExportLoading(true);
    try {
      await generateInsightsPPTX(summary, companyName);
    } catch (err) {
      console.error("PPTX export failed:", err);
      toast.error(err instanceof Error ? err.message : "Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const ContextCard = ({ ctx }: { ctx: InsightsContext }) => {
    const o = ctx.overall;
    const pl = ctx.profit_loss;
    const ho = ctx.highest_order;
    const trendUp = pl?.trend === "up";
    return (
      <div className="mt-3 rounded-xl border border-[#dde5f4] bg-white overflow-hidden text-xs">
        <div className="px-3 py-2 bg-[#f0f4fb] font-semibold text-[#143887] flex items-center gap-1.5">
          <Sparkles size={11} />
          Data snapshot
        </div>
        <div className="grid grid-cols-2 gap-px bg-[#eef1f8]">
          {[
            { label: "Total Orders", value: o.total_orders },
            { label: "Revenue", value: `₦${o.total_revenue.toLocaleString()}` },
            { label: "Completed", value: o.completed_orders },
            { label: "Completion Rate", value: `${o.completion_rate}%` },
          ].map((item) => (
            <div key={item.label} className="bg-white px-3 py-2">
              <p className="text-gray-400">{item.label}</p>
              <p className="font-semibold text-[#143887] mt-0.5">{item.value}</p>
            </div>
          ))}
        </div>

        {pl && (
          <div className="px-3 py-2 border-t border-[#eef1f8] flex items-center justify-between">
            <div>
              <p className="text-gray-400 mb-0.5">This Month</p>
              <p className="font-semibold text-[#143887]">₦{pl.this_month_revenue.toLocaleString()}</p>
            </div>
            <div className="text-right">
              <p className="text-gray-400 mb-0.5">vs Last Month</p>
              <p className={`font-semibold flex items-center gap-0.5 justify-end ${trendUp ? "text-green-600" : "text-red-500"}`}>
                {trendUp ? "▲" : "▼"} {Math.abs(pl.mom_change_percent)}%
              </p>
            </div>
            <div className="text-right">
              <p className="text-gray-400 mb-0.5">Collection Rate</p>
              <p className="font-semibold text-[#143887]">{pl.collection_rate}%</p>
            </div>
          </div>
        )}

        {ho && (
          <div className="px-3 py-2 border-t border-[#eef1f8]">
            <p className="text-gray-400 mb-0.5">Highest Order</p>
            <p className="font-medium text-gray-700">
              {ho.order_number} · ₦{ho.amount.toLocaleString()} · {ho.payment_status}
            </p>
          </div>
        )}

        {ctx.top_drivers?.length > 0 && (
          <div className="px-3 py-2 border-t border-[#eef1f8]">
            <p className="text-gray-400 mb-1">Top Driver</p>
            <p className="font-medium text-gray-700">
              {ctx.top_drivers[0].driver_name} · {ctx.top_drivers[0].completed_jobs} jobs · {formatDriverRating(ctx.top_drivers[0].avg_rating)}
            </p>
          </div>
        )}
      </div>
    );
  };

  const suggestionChips = [
    "Am I making profit or loss this month?",
    "How are my orders performing this week?",
    "What's my highest order this period?",
    "Summarize my revenue trend",
    "How many days left on my subscription?",
    "Which drivers have the most trips?",
    "What's my collection rate this month?",
    "How many branches and staff do I have?",
    "Which driver has completed the most deliveries?",
    "Which driver has completed the least deliveries?",
    "Do I have any drivers who haven't made a delivery yet?",
  ];

  if (!hasAccess) {
    return (
      <DashboardLayout
        title="AI Insights"
        description="Powered by Fleetrify — instant AI-driven fleet answers."
      >
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <Lock size={28} className="text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            Individual Business Plan Required
          </h2>
          <p className="text-sm text-gray-500 max-w-sm mb-6">
            AI Insights is available exclusively on the Individual Business plan.
            Upgrade your subscription to unlock instant AI-driven fleet analysis.
          </p>
          <button
            onClick={() => navigate(FLEETRIFYROUTEPATH.SETTINGS)}
            className="px-5 py-2.5 bg-[#143887] text-white text-sm font-medium rounded-lg hover:bg-[#0f2d6b] transition-colors"
          >
            Upgrade Plan
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="AI Insights"
      description="Powered by Fleetrify — ask questions about your fleet and get instant AI-driven answers."
    >
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6 pb-10">
        {/* ── LEFT COLUMN: Summary + Export ── */}
        <div className="xl:col-span-1 flex flex-col gap-6 self-start">
          {/* Summary card */}
          <FleetrifyCard cardContainerClass="!p-5 gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-[#143887]" />
                <span className="text-sm font-semibold text-[#143887]">AI Summary</span>
              </div>
              <button
                onClick={() => refetchSummary()}
                className="p-1.5 rounded-lg hover:bg-[#f0f4fb] transition-colors"
                title="Refresh summary"
              >
                <RefreshCw size={14} className={`text-[#143887] ${summaryLoading ? "animate-spin" : ""}`} />
              </button>
            </div>

            {summaryLoading ? (
              <div className="space-y-2 animate-pulse">
                {[80, 65, 90, 55].map((w, i) => (
                  <div key={i} className="h-3 bg-[#e8edf7] rounded" style={{ width: `${w}%` }} />
                ))}
              </div>
            ) : summary ? (
              <>
                {/* Overall stat grid */}
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "Total Orders", value: summary.overall.total_orders },
                    { label: "Total Revenue", value: `₦${summary.overall.total_revenue.toLocaleString()}` },
                    { label: "Collected", value: `₦${(summary.overall.collected_revenue ?? 0).toLocaleString()}` },
                    { label: "Uncollected", value: `₦${(summary.overall.uncollected_revenue ?? 0).toLocaleString()}` },
                    { label: "Completed", value: summary.overall.completed_orders },
                    { label: "Completion Rate", value: `${summary.overall.completion_rate}%` },
                    { label: "Pending", value: summary.overall.pending_orders },
                    { label: "Cancellation Rate", value: `${summary.overall.cancellation_rate}%` },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-[#f5f7fb] rounded-xl px-3 py-2.5">
                      <p className="text-[10px] text-gray-400 mb-0.5">{stat.label}</p>
                      <p className="text-sm font-semibold text-[#143887]">{stat.value}</p>
                    </div>
                  ))}
                </div>

                {/* Company profile */}
                {summary.company_profile && (
                  <div className="mt-1">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                      Company Profile
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: "Branches", value: summary.company_profile.total_branches },
                        { label: "Staff", value: summary.company_profile.total_staff },
                        { label: "Services", value: summary.company_profile.total_services },
                      ].map((s) => (
                        <div key={s.label} className="bg-[#f5f7fb] rounded-xl px-2 py-2 text-center">
                          <p className="text-[10px] text-gray-400">{s.label}</p>
                          <p className="text-sm font-semibold text-[#143887]">{s.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Subscription */}
                {summary.subscription && (
                  <div className={`rounded-xl px-3 py-2.5 text-xs ${summary.subscription.days_remaining <= 5 ? "bg-red-50 border border-red-100" : "bg-[#f5f7fb]"}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 font-medium">{summary.subscription.plan_name}</span>
                      <span className={`font-semibold ${summary.subscription.days_remaining <= 5 ? "text-red-500" : "text-[#143887]"}`}>
                        {summary.subscription.days_remaining}d left
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5 capitalize">
                      {summary.subscription.billing_cycle} · {summary.subscription.status}
                    </p>
                  </div>
                )}

                {/* Profit/Loss trend */}
                {summary.profit_loss && (
                  <div className="mt-1">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                      Monthly P&amp;L
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-[#f5f7fb] rounded-xl px-3 py-2.5">
                        <p className="text-[10px] text-gray-400 mb-0.5">This Month</p>
                        <p className="text-sm font-semibold text-[#143887]">₦{summary.profit_loss.this_month_revenue.toLocaleString()}</p>
                      </div>
                      <div className="bg-[#f5f7fb] rounded-xl px-3 py-2.5">
                        <p className="text-[10px] text-gray-400 mb-0.5">MoM Change</p>
                        <p className={`text-sm font-semibold ${summary.profit_loss.trend === "up" ? "text-green-600" : "text-red-500"}`}>
                          {summary.profit_loss.trend === "up" ? "▲" : "▼"} {Math.abs(summary.profit_loss.mom_change_percent)}%
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Highest order */}
                {summary.highest_order && (
                  <div className="bg-[#f5f7fb] rounded-xl px-3 py-2.5 text-xs">
                    <p className="text-[10px] text-gray-400 mb-0.5 font-semibold uppercase tracking-wide">Highest Order</p>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{summary.highest_order.order_number}</span>
                      <span className="font-semibold text-[#143887]">₦{summary.highest_order.amount.toLocaleString()}</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5 capitalize">{summary.highest_order.payment_status} · {summary.highest_order.date}</p>
                  </div>
                )}

                {/* Most requested services */}
                {summary.most_requested?.length > 0 && (
                  <div className="mt-1">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                      Top Services
                    </p>
                    <div className="space-y-1.5">
                      {summary.most_requested.map((svc) => (
                        <div key={svc.service_id} className="flex items-center justify-between text-xs">
                          <span className="text-gray-600 truncate max-w-[65%]">{svc.service_name}</span>
                          <span className="font-semibold text-[#143887] shrink-0">
                            {svc.total_orders} orders
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Period label */}
                {(summary.period.from || summary.period.to) && (
                  <p className="text-[10px] text-gray-400">
                    Period: {summary.period.from ?? "–"} → {summary.period.to ?? "–"}
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-400">No summary available yet.</p>
            )}
          </FleetrifyCard>

          {/* Export card */}
          <FleetrifyCard cardContainerClass="!p-5 gap-3 !min-h-[220px] !h-auto self-start">
            <div className="flex items-center gap-2 mb-1">
              <Download size={16} className="text-[#143887]" />
              <span className="text-sm font-semibold text-[#143887]">Export Report</span>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Download a PowerPoint presentation with charts for revenue, orders, top drivers, services, and cancellation trends — branded with your company name.
            </p>
            <button
              onClick={handleExport}
              disabled={exportLoading || !summary}
              className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-[#143887] text-white text-sm font-medium hover:bg-[#0f2d6e] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {exportLoading ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Download size={15} />
              )}
              {exportLoading ? "Generating…" : "Export as PowerPoint"}
            </button>
          </FleetrifyCard>
        </div>

        {/* ── RIGHT COLUMN: AI Chat ── */}
        <FleetrifyCard cardContainerClass="xl:col-span-2 !p-0 overflow-hidden flex flex-col min-h-[560px]">
          {/* Chat header */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-[#f0f0f0] bg-gradient-to-r from-[#f5f7fb] to-white">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#143887] to-[#4a6fc4] flex items-center justify-center">
              <Bot size={18} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#143887]">Fleetrify AI</p>
              <p className="text-xs text-gray-400">Powered by Fleetrify· Always available</p>
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs text-gray-400">Online</span>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 hide-scrollbar">
            <AnimatePresence initial={false}>
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {msg.role === "assistant" && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#143887] to-[#4a6fc4] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bot size={14} className="text-white" />
                    </div>
                  )}

                  <div
                    className={`max-w-[78%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      msg.role === "user"
                        ? "bg-[#143887] text-white rounded-br-sm"
                        : "bg-[#f5f7fb] text-gray-700 rounded-bl-sm"
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                    {msg.context && <ContextCard ctx={msg.context} />}
                    <p
                      className={`text-[10px] mt-1.5 ${
                        msg.role === "user" ? "text-blue-200" : "text-gray-400"
                      }`}
                    >
                      {msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>

                  {msg.role === "user" && (
                    <div className="w-8 h-8 rounded-full bg-[#e8edf7] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User size={14} className="text-[#143887]" />
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Typing indicator */}
            {askLoading && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex gap-3 justify-start"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#143887] to-[#4a6fc4] flex items-center justify-center flex-shrink-0">
                  <Bot size={14} className="text-white" />
                </div>
                <div className="bg-[#f5f7fb] rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1.5">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="w-2 h-2 rounded-full bg-[#143887] opacity-60 animate-bounce"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestion chips */}
          {messages.length <= 1 && (
            <div className="px-5 pb-2 flex flex-wrap gap-2">
              {suggestionChips.map((chip) => (
                <button
                  key={chip}
                  onClick={() => setQuestion(chip)}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#d0daf0] text-[#143887] bg-white hover:bg-[#f0f4fb] transition-colors"
                >
                  {chip}
                </button>
              ))}
            </div>
          )}

          {/* Input bar */}
          <div className="px-4 pt-3 pb-3 border-t border-[#f0f0f0] bg-white space-y-2">
            {/* Date range toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDateFilter((v) => !v)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  showDateFilter || dateFrom || dateTo
                    ? "bg-[#143887] text-white border-[#143887]"
                    : "border-[#d0daf0] text-[#143887] bg-white hover:bg-[#f0f4fb]"
                }`}
              >
                <CalendarDays size={12} />
                {dateFrom || dateTo
                  ? `${dateFrom || "…"} → ${dateTo || "…"}`
                  : "Filter by date"}
              </button>
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(""); setDateTo(""); }}
                  className="text-xs text-gray-400 hover:text-red-400 flex items-center gap-1 transition-colors"
                >
                  <X size={11} /> Clear
                </button>
              )}
            </div>

            <AnimatePresence>
              {showDateFilter && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="flex items-center gap-2 pb-1">
                    <div className="flex-1">
                      <label className="text-[10px] text-gray-400 mb-1 block">From</label>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        max={dateTo || undefined}
                        className="w-full text-xs border border-[#d0daf0] rounded-lg px-3 py-2 outline-none focus:border-[#143887] text-gray-700 bg-[#f5f7fb]"
                      />
                    </div>
                    <span className="text-gray-400 text-xs mt-4">→</span>
                    <div className="flex-1">
                      <label className="text-[10px] text-gray-400 mb-1 block">To</label>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        min={dateFrom || undefined}
                        className="w-full text-xs border border-[#d0daf0] rounded-lg px-3 py-2 outline-none focus:border-[#143887] text-gray-700 bg-[#f5f7fb]"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-end gap-3 bg-[#f5f7fb] rounded-2xl px-4 py-2">
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask anything about your fleet, orders, revenue…"
                rows={1}
                className="flex-1 bg-transparent resize-none outline-none text-sm text-gray-700 placeholder-gray-400 max-h-32 py-1"
                style={{ lineHeight: "1.5" }}
              />
              <button
                onClick={handleSend}
                disabled={!question.trim() || askLoading}
                className="mb-1 w-8 h-8 rounded-full bg-[#143887] flex items-center justify-center hover:bg-[#0f2d6e] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              >
                {askLoading ? (
                  <Loader2 size={14} className="text-white animate-spin" />
                ) : (
                  <Send size={14} className="text-white" />
                )}
              </button>
            </div>
            <p className="text-[10px] text-gray-400 text-center mt-1.5">
              Press Enter to send · Shift+Enter for new line
            </p>
          </div>
        </FleetrifyCard>
      </div>

      {/* ── Driver Performance ── */}
      {summary?.driver_performance && (
        <div className="pb-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FleetrifyCard cardContainerClass="!p-5 gap-1">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                Most Active Driver
              </p>
              {summary.driver_performance.most_active_driver ? (
                <>
                  <p className="text-sm font-semibold text-[#143887] mt-1">
                    {summary.driver_performance.most_active_driver.driver_name}
                  </p>
                  <p className="text-xs text-gray-400">
                    {summary.driver_performance.most_active_driver.branch_name}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs font-medium text-gray-600">
                      {summary.driver_performance.most_active_driver.total_deliveries} deliveries
                    </span>
                    <span className="text-xs font-medium text-gray-600">
                      {formatDriverRating(summary.driver_performance.most_active_driver.avg_rating)}
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-400 mt-1">No data available.</p>
              )}
            </FleetrifyCard>

            <FleetrifyCard cardContainerClass="!p-5 gap-1">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                Least Active Driver
              </p>
              {summary.driver_performance.least_active_driver ? (
                <>
                  <p className="text-sm font-semibold text-[#143887] mt-1">
                    {summary.driver_performance.least_active_driver.driver_name}
                  </p>
                  <p className="text-xs text-gray-400">
                    {summary.driver_performance.least_active_driver.branch_name}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs font-medium text-gray-600">
                      {summary.driver_performance.least_active_driver.total_deliveries} deliveries
                    </span>
                    <span className="text-xs font-medium text-gray-600">
                      {formatDriverRating(summary.driver_performance.least_active_driver.avg_rating)}
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-400 mt-1">No data available.</p>
              )}
            </FleetrifyCard>
          </div>

          <FleetrifyCard cardContainerClass="!p-5 gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-[#143887]">Driver Leaderboard</span>
              <span className="text-xs text-gray-400">
                {summary.driver_performance.total_drivers} drivers · avg{" "}
                {summary.driver_performance.average_deliveries_per_driver} deliveries each
              </span>
            </div>
            {summary.driver_performance.drivers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] text-gray-400 uppercase tracking-wide border-b border-[#eef1f8]">
                      <th className="py-2 pr-3 font-semibold">#</th>
                      <th className="py-2 pr-3 font-semibold">Driver</th>
                      <th className="py-2 pr-3 font-semibold">Branch</th>
                      <th className="py-2 pr-3 font-semibold">Deliveries</th>
                      <th className="py-2 pr-3 font-semibold">Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.driver_performance.drivers.map((d, i) => (
                      <tr key={d.driver_id} className="border-b border-[#f5f7fb] last:border-0">
                        <td className="py-2 pr-3 text-gray-400">{i + 1}</td>
                        <td className="py-2 pr-3 font-medium text-gray-700">{d.driver_name}</td>
                        <td className="py-2 pr-3 text-gray-500">{d.branch_name}</td>
                        <td className="py-2 pr-3 text-gray-600">{d.total_deliveries}</td>
                        <td className="py-2 pr-3 text-gray-600">{formatDriverRating(d.avg_rating)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-2">No driver assigned at the moment.</p>
            )}
          </FleetrifyCard>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Insights;
